import {
  FC,
  useEffect,
  memo,
  useCallback,
  useRef,
  useState,
  useMemo,
} from "react";
import {
  Badge,
  Box,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Chip,
  Tabs,
  Tab,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import LaunchIcon from "@mui/icons-material/Launch";
import RefreshIcon from "@mui/icons-material/Refresh";
import CancelIcon from "@mui/icons-material/Cancel";
import AdminPage from "../AdminPage";
import { apiClient } from "../../utils/apiClient";
import { format } from "date-fns";
import RecurringWorkflowsTab from "./RecurringWorkflowsTab";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { compactTableStyles } from "../theme";
import type {
  WorkflowListItem,
  WorkflowsListResponse,
  StartWorkflowResponse,
  WorkflowTypesResponse,
} from "../../types/workflow";
import { useConfirmationDialog } from "../../components/ConfirmationDialog";
import TokenDisplay from "../../components/TokenDisplay";

interface WorkflowTestData {
  name: string;
  description?: string;
  data: any;
}

interface WorkflowType {
  name: string;
  description?: string;
  testData: WorkflowTestData[];
}

const WorkflowStatus: FC<{ status: string }> = ({ status }) => {
  const getStatusColor = () => {
    switch (status) {
      case "RUNNING":
        return "primary";
      case "COMPLETED":
        return "success";
      case "FAILED":
        return "error";
      case "CANCELLED":
      case "TERMINATED":
        return "warning";
      default:
        return "default";
    }
  };

  return (
    <Chip
      label={status}
      color={getStatusColor()}
      size="small"
      sx={compactTableStyles.chip}
    />
  );
};

const WorkflowsTable = memo(
  ({ workflows }: { workflows: WorkflowListItem[] }) => {
    const navigate = useNavigate();
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(
      null
    );

    const handleMenuOpen = (
      event: React.MouseEvent<HTMLElement>,
      workflowId: string
    ) => {
      event.stopPropagation();
      setMenuAnchorEl(event.currentTarget);
      setSelectedWorkflowId(workflowId);
    };

    const handleMenuClose = () => {
      setMenuAnchorEl(null);
      setSelectedWorkflowId(null);
    };

    const handleCancel = async () => {
      if (!selectedWorkflowId) return;
      handleMenuClose();
      try {
        const response = await apiClient.post(
          `/api/workflows/workflow/${selectedWorkflowId}/cancel`
        );
        console.log(response);
        // Refresh the list
        window.location.reload();
      } catch (error) {
        console.error("Failed to cancel workflow:", error);
      }
    };

    const handleTerminate = async () => {
      if (!selectedWorkflowId) return;
      handleMenuClose();
      try {
        const response = await apiClient.post(
          `/api/workflows/workflow/${selectedWorkflowId}/terminate`
        );
        console.log(response);
        // Refresh the list
        window.location.reload();
      } catch (error) {
        console.error("Failed to terminate workflow:", error);
      }
    };

    const handleViewGptRequests = () => {
      if (!selectedWorkflowId) return;
      handleMenuClose();
      navigate(`/admin/requests?workflowId=${selectedWorkflowId}`);
    };

    const handleViewActionLogs = () => {
      if (!selectedWorkflowId) return;
      handleMenuClose();
      navigate(`/admin/action-logs?workflowId=${selectedWorkflowId}`);
    };

    const handleRowClick = (workflow: WorkflowListItem) => {
      if (workflow.workflowUrl) {
        window.open(workflow.workflowUrl, "_blank");
      }
    };

    return (
      <TableContainer component={Paper}>
        <Table {...compactTableStyles.table}>
          <TableHead sx={compactTableStyles.tableHead}>
            <TableRow>
              <TableCell>Workflow ID</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Tokens</TableCell>
              <TableCell>Start Time</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody sx={compactTableStyles.tableBody}>
            {workflows.map((workflow) => (
              <TableRow
                key={workflow.workflowId}
                hover
                onClick={() => handleRowClick(workflow)}
                style={{ cursor: "pointer" }}
              >
                <TableCell
                  sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography
                      variant="body2"
                      sx={{ whiteSpace: "normal", wordBreak: "break-all" }}
                    >
                      {workflow.workflowId}
                    </Typography>
                    {workflow.workflowUrl && (
                      <LaunchIcon
                        sx={{ fontSize: "0.875rem" }}
                        color="action"
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                >
                  {workflow.type}
                </TableCell>
                <TableCell>
                  <WorkflowStatus status={workflow.status} />
                </TableCell>
                <TableCell>
                  {workflow.result?.promptTokens ||
                  workflow.result?.outputTokens ? (
                    <TokenDisplay
                      promptTokens={workflow.result.promptTokens || 0}
                      outputTokens={workflow.result.outputTokens || 0}
                      variant="arrows"
                    />
                  ) : (
                    <Box
                      component="span"
                      sx={{
                        color: "text.disabled",
                        fontSize: "0.85em",
                      }}
                    >
                      -
                    </Box>
                  )}
                </TableCell>
                <TableCell>
                  {format(new Date(workflow.startTime), "HH:mm - yyyy/MM/dd")}
                </TableCell>
                <TableCell>{workflow.executionTime || "-"}</TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, workflow.workflowId)}
                    sx={compactTableStyles.iconButton}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleViewGptRequests}>View GPT Requests</MenuItem>
          <MenuItem onClick={handleViewActionLogs}>View Action Logs</MenuItem>
          <MenuItem onClick={handleCancel}>Cancel</MenuItem>
          <MenuItem onClick={handleTerminate}>Terminate</MenuItem>
        </Menu>
      </TableContainer>
    );
  }
);

const WorkflowsList: FC = (): JSX.Element => {
  const { openDialog, openAlert, ConfirmationDialog } = useConfirmationDialog();
  const [workflowTypes, setWorkflowTypes] = useState<WorkflowType[]>([]);
  const [selectedWorkflowType, setSelectedWorkflowType] = useState<string>("");
  const [selectedTestData, setSelectedTestData] = useState<string>("");
  const [testDataJson, setTestDataJson] = useState<string>("");
  const [workflows, setWorkflows] = useState<WorkflowListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  // Initialize tab value from URL params
  const getInitialTab = () => {
    const tab = searchParams.get("tab");
    if (tab === "recurring") return 1;
    return 0; // default to 'running'
  };

  const [tabValue, setTabValue] = useState(getInitialTab());
  const isMounted = useRef(true);

  // Sync tab value with URL params
  useEffect(() => {
    const tab = searchParams.get("tab");
    const newTabValue = tab === "recurring" ? 1 : 0;
    if (newTabValue !== tabValue) {
      setTabValue(newTabValue);
    }
  }, [searchParams]);

  // Fetch workflow types
  useEffect(() => {
    const fetchWorkflowTypes = async () => {
      try {
        const response = await apiClient.get<WorkflowTypesResponse>(
          "/api/workflows/workflow-types"
        );
        setWorkflowTypes(response.workflowTypes);
      } catch (error) {
        console.error("Error fetching workflow types:", error);
      }
    };
    fetchWorkflowTypes();
  }, []);

  // Fetch workflows list
  const fetchWorkflows = useCallback(async () => {
    try {
      const response = await apiClient.get<WorkflowsListResponse>(
        "/api/workflows/workflows"
      );
      setWorkflows(response.workflows);
    } catch (error) {
      console.error("Error fetching workflows:", error);
    }
  }, []);

  // Fetch on mount
  useEffect(() => {
    fetchWorkflows();
  }, [fetchWorkflows]);

  // Get test data options for selected workflow type
  const testDataOptions = useMemo(() => {
    const workflowType = workflowTypes.find(
      (wt) => wt.name === selectedWorkflowType
    );
    return workflowType?.testData || [];
  }, [selectedWorkflowType, workflowTypes]);

  // Update JSON when test data selection changes
  useEffect(() => {
    const selectedData = testDataOptions.find(
      (td) => td.name === selectedTestData
    );
    if (selectedData) {
      setTestDataJson(JSON.stringify(selectedData.data, null, 2));
    } else {
      setTestDataJson("");
    }
  }, [selectedTestData, testDataOptions]);

  const handleStartWorkflow = async () => {
    if (!selectedWorkflowType) return;

    setLoading(true);
    try {
      let testParams = {};
      if (testDataJson) {
        try {
          testParams = JSON.parse(testDataJson);
        } catch (error) {
          openAlert("Invalid JSON in test data", {
            title: "Error",
            confirmColor: "error",
          });
          setLoading(false);
          return;
        }
      }

      const response = await apiClient.post<StartWorkflowResponse>(
        `/api/workflows/start/${selectedWorkflowType}`,
        { testParams }
      );

      console.log(response);

      // Refresh workflows list
      fetchWorkflows();

      // Reset form
      setSelectedWorkflowType("");
      setSelectedTestData("");
      setTestDataJson("");
    } catch (error: any) {
      console.error("Failed to start workflow:", error);
      openAlert(error.message || "Failed to start workflow", {
        title: "Error",
        confirmColor: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    // Update URL with new tab parameter
    const newSearchParams = new URLSearchParams(searchParams);
    if (newValue === 0) {
      newSearchParams.set("tab", "running");
    } else if (newValue === 1) {
      newSearchParams.set("tab", "recurring");
    }
    setSearchParams(newSearchParams);
  };

  const handleCancelAll = async () => {
    // Filter only running workflows that can be cancelled
    const runningWorkflows = workflows.filter((w) => w.status === "RUNNING");

    if (runningWorkflows.length === 0) {
      openAlert("No running workflows to cancel.", {
        title: "Information",
        confirmColor: "info",
      });
      return;
    }

    openDialog(
      `Are you sure you want to cancel ALL ${runningWorkflows.length} running workflows?`,
      async () => {
        try {
          // Cancel all running workflows one by one
          const cancelPromises = runningWorkflows.map((workflow) =>
            apiClient.post(
              `/api/workflows/workflow/${workflow.workflowId}/cancel`
            )
          );

          await Promise.all(cancelPromises);
          fetchWorkflows();
          openAlert(
            `Successfully cancelled ${runningWorkflows.length} workflows.`,
            { title: "Success", confirmColor: "success" }
          );
        } catch (error) {
          console.error("Failed to cancel all workflows:", error);
          openAlert("Failed to cancel some workflows. Please try again.", {
            title: "Error",
            confirmColor: "error",
          });
        }
      },
      {
        title: "Cancel All Workflows",
        confirmText: "Cancel All",
        confirmColor: "error",
      }
    );
  };

  return (
    <AdminPage title="⚙️ Temporal Workflows">
      <Box sx={{ p: 2 }}>
        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="workflow tabs"
          >
            <Tab
              label={
                <Badge
                  badgeContent={
                    workflows.filter((w) => w.status === "RUNNING").length
                  }
                  color="primary"
                  invisible={
                    workflows.filter((w) => w.status === "RUNNING").length === 0
                  }
                >
                  Running temporal Workflows
                </Badge>
              }
            />
            <Tab label="Dobby Workflows" />
          </Tabs>
        </Paper>

        {/* Tab Content */}
        {tabValue === 0 && (
          <>
            {/* Start Workflow Section */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "flex-start",
                  flexWrap: "wrap",
                }}
              >
                {/* Workflow Type Selection */}
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>Workflow Type</InputLabel>
                  <Select
                    value={selectedWorkflowType}
                    onChange={(e: SelectChangeEvent) =>
                      setSelectedWorkflowType(e.target.value)
                    }
                    label="Workflow Type"
                  >
                    {workflowTypes.map((wt) => (
                      <MenuItem key={wt.name} value={wt.name}>
                        {wt.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Test Data Selection */}
                {selectedWorkflowType && testDataOptions.length > 0 && (
                  <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Test Data</InputLabel>
                    <Select
                      value={selectedTestData}
                      onChange={(e: SelectChangeEvent) =>
                        setSelectedTestData(e.target.value)
                      }
                      label="Test Data"
                    >
                      {testDataOptions.map((td) => (
                        <MenuItem key={td.name} value={td.name}>
                          {td.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {/* Start Button */}
                <IconButton
                  onClick={handleStartWorkflow}
                  color="primary"
                  disabled={!selectedWorkflowType || loading}
                  sx={{ mt: 1 }}
                >
                  <PlayArrowIcon />
                </IconButton>

                {/* Refresh Button */}
                <IconButton
                  onClick={fetchWorkflows}
                  color="default"
                  sx={{ mt: 1, ml: "auto" }}
                >
                  <RefreshIcon />
                </IconButton>
              </Box>

              {/* Test Data JSON Editor */}
              {selectedWorkflowType && (
                <Box sx={{ mt: 2 }}>
                  <TextField
                    label="Test Data (JSON)"
                    multiline
                    rows={10}
                    fullWidth
                    value={testDataJson}
                    onChange={(e) => setTestDataJson(e.target.value)}
                    sx={{
                      "& .MuiInputBase-input": {
                        fontFamily: "monospace",
                        fontSize: 12,
                      },
                    }}
                  />
                </Box>
              )}
            </Paper>

            {/* Workflows List */}
            <Paper>
              <Box
                sx={{
                  p: 2,
                  borderBottom: 1,
                  borderColor: "divider",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <h3 style={{ margin: 0 }}>Running Workflows</h3>
                <IconButton
                  onClick={handleCancelAll}
                  color="error"
                  disabled={
                    workflows.filter((w) => w.status === "RUNNING").length === 0
                  }
                  title="Cancel all running workflows"
                >
                  <CancelIcon />
                </IconButton>
              </Box>
              {workflows.length > 0 ? (
                <WorkflowsTable workflows={workflows} />
              ) : (
                <Box
                  sx={{ p: 3, textAlign: "center", color: "text.secondary" }}
                >
                  No workflows found
                </Box>
              )}
            </Paper>
          </>
        )}

        {/* Recurring Workflows Tab */}
        {tabValue === 1 && (
          <Box>
            <RecurringWorkflowsTab />
          </Box>
        )}
      </Box>

      {/* Confirmation Dialog */}
      <ConfirmationDialog />
    </AdminPage>
  );
};

export default memo(WorkflowsList);
